import React, {
  memo,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useKeydown } from '../../hooks/useKeydown';
import { ListViewProps, ItemProps } from './ListView.types';

export const ListView: React.FC<ListViewProps> = memo(
  ({
    id,
    uniqueKey = 'list-',
    listType = 'horizontal',
    nativeControle = false,
    itemsCount,
    itemsTotal,
    buffer,
    itemWidth,
    itemHeight,
    gap,
    isActive,
    initialActiveIndex = 0,
    onBackScrollIndex = null,
    startScrollIndex = 0,
    stopScrollIndex = itemsTotal - 1,
    direction = 'ltr',
    withTitle = false,
    rowGap = 0,
    debounce = 200,
    onIndexChange,
    onMouseEnter,
    onUp,
    onDown,
    onLeft,
    onRight,
    onBack,
    renderItem,
    data,
    onOk,
    withTransition = true,
  }) => {
    const scrollViewRef = useRef<HTMLDivElement>(null);
    const [startIndex, setStartIndex] = useState(0);
    const [activeIndex, setActiveIndex] = useState(initialActiveIndex);

    const changeStartIndex = useCallback(
      (index: number) => {
        index -= startScrollIndex;
        if (index > stopScrollIndex) index = stopScrollIndex;
        if (index < 0) index = 0;
        setStartIndex(index);
      },
      [startScrollIndex, stopScrollIndex]
    );

    const setInitialActiveIndex = useCallback(
      (index: number) => {
        setActiveIndex(index);
        changeStartIndex(index);
      },
      [changeStartIndex]
    );

    useEffect(() => {
      setInitialActiveIndex(initialActiveIndex);
    }, [id, initialActiveIndex, setInitialActiveIndex]);

    const scrollToIndex = useCallback(
      (index: number) => {
        setActiveIndex(index);
        changeStartIndex(index);

        setTimeout(() => {
          setStartIndex((prevStartIndex) => prevStartIndex);
        }, 0);
      },
      [changeStartIndex]
    );

    const next = useCallback(
      (count = 1) => {
        setActiveIndex((index) => {
          if (index === itemsTotal - 1) {
            listType === 'horizontal'
              ? onRight && requestAnimationFrame(onRight)
              : onDown && requestAnimationFrame(onDown);
          } else {
            index += count;
            if (index > itemsTotal - 1) index = itemsTotal - 1;
          }

          changeStartIndex(index);

          return index;
        });
      },
      [itemsTotal, listType, onRight, onDown, changeStartIndex]
    );

    const prev = useCallback(
      (count = 1) => {
        setActiveIndex((index) => {
          if (index === 0) {
            listType === 'horizontal'
              ? onLeft && requestAnimationFrame(onLeft)
              : onUp && requestAnimationFrame(onUp);
          } else {
            index -= count;
            if (index < 0) index = 0;
            // if (onLeft) { // TODO: maybe need to call when listType is vertical
            //   onLeft();
            // }
          }

          changeStartIndex(index);

          return index;
        });
      },
      [listType, onLeft, onUp, changeStartIndex]
    );

    const back = useCallback(() => {
      if (onBackScrollIndex !== null) {
        scrollToIndex(onBackScrollIndex);
        setActiveIndex(onBackScrollIndex);
      } else {
        scrollToIndex(0);
        setActiveIndex(0);
      }

      onBack && requestAnimationFrame(onBack);
    }, [onBackScrollIndex, scrollToIndex, onBack]);

    const onMouseEnterItem = useCallback(
      (index: number) => {
        setActiveIndex(index);
        onMouseEnter?.(index);
      },
      [onMouseEnter]
    );

    const getItemStyle = useCallback(
      (index: number): React.CSSProperties => {
        return {
          position: 'absolute',
          width: `${itemWidth}rem`,
          height: `${itemHeight}rem`,
          ...(listType === 'horizontal'
            ? {
                [direction === 'rtl' ? 'right' : 'left']: `${
                  index * (itemWidth + (gap || 0))
                }rem`,
                top: 0,
              }
            : {
                left: 0,
                top: `${index * (itemHeight + (gap || 0))}rem`,
              }),
        };
      },
      [itemWidth, itemHeight, listType, direction, gap]
    );

    const renderItems = useCallback(() => {
      const items: React.ReactNode[] = [];
      const start = startIndex - buffer;
      const end = startIndex + itemsCount + buffer;

      for (let i = start; i < end; i++) {
        if (i >= 0 && i < itemsTotal) {
          const itemProps: ItemProps = {
            key: `${uniqueKey}${i}`,
            index: i,
            style: getItemStyle(i),
            isActive: i === activeIndex && isActive,
            item: data[i],
            onUp,
            onDown,
            onLeft,
            onRight,
            onMouseEnter: () => onMouseEnterItem(i),
          };
          items.push(renderItem(itemProps));
        }
      }
      return items;
    }, [
      startIndex,
      buffer,
      itemsCount,
      itemsTotal,
      uniqueKey,
      getItemStyle,
      activeIndex,
      isActive,
      onUp,
      onDown,
      onLeft,
      onRight,
      onMouseEnterItem,
      renderItem,
      data,
    ]);

    useEffect(() => {
      const applyTransform = () => {
        if (!scrollViewRef.current) return;

        const verticalSpacing = itemHeight + rowGap;

        scrollViewRef.current.style.transition = withTransition
          ? 'transform 0.5s cubic-bezier(0.4, 0.8, 0.5, 1)'
          : 'none';

        const transform =
          listType === 'horizontal'
            ? `translate3d(${direction === 'ltr' ? '-' : ''}${
                startIndex * (itemWidth + (gap || 0))
              }rem, 0, 0)`
            : `translate3d(0, -${startIndex * verticalSpacing}rem, 0)`;

        scrollViewRef.current.style.transform = transform;
        scrollViewRef.current.style.webkitTransform = transform;
      };

      applyTransform();
    }, [
      startIndex,
      listType,
      direction,
      itemWidth,
      itemHeight,
      gap,
      withTitle,
      rowGap,
      withTransition,
    ]);

    const handleOk = useCallback(() => {
      onOk?.(data[activeIndex], activeIndex);
    }, [activeIndex, onOk, data]);

    const keyDownOptions = useMemo(
      () => ({
        isActive: isActive && nativeControle,
        left: () => listType === 'horizontal' && prev(),
        right: () => listType === 'horizontal' && next(),
        up: () => listType !== 'horizontal' && prev(),
        down: () => listType !== 'horizontal' && next(),
        channel_up: () => prev(itemsCount),
        channel_down: () => next(itemsCount),
        back,
        ok: handleOk,
        debounce,
      }),
      [
        isActive,
        nativeControle,
        listType,
        prev,
        next,
        itemsCount,
        back,
        handleOk,
        debounce,
      ]
    );

    useKeydown(keyDownOptions);

    const parentStyle = useMemo(
      () => ({
        [listType === 'horizontal' ? 'height' : 'width']: `${
          listType === 'horizontal' ? itemHeight : itemWidth
        }rem`,
      }),
      [listType, itemHeight, itemWidth]
    );

    useEffect(() => {
      onIndexChange?.(activeIndex);
    }, [activeIndex, onIndexChange]);

    return (
      <div className={`scroll-view-parent ${listType}`} style={parentStyle}>
        <div
          className={`scroll-view list-view ${
            direction === 'rtl' ? 'rtl-list-view' : ''
          }`}
          ref={scrollViewRef}
        >
          {renderItems()}
        </div>
      </div>
    );
  }
);
